// common/Citiescommondropdown.jsx
import React, { useState, useRef, useEffect, useMemo } from 'react';

const Citiescommondropdown = ({ 
  value, 
  onChange, 
  options = [], 
  placeholder = 'City',
  className = '',
  disabled = false,
  showSearch = false,
  icon = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && showSearch && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isOpen, showSearch]);

  // Find selected option
  const selectedOption = useMemo(() => {
    return options.find(opt => opt.value === value);
  }, [options, value]);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!showSearch || !searchTerm) return options;
    return options.filter(option =>
      option.label?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm, showSearch]);

  // Handle option selection
  const handleOptionClick = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger button */}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className="flex-1 bg-transparent text-gray-700 text-sm font-medium font-['Inter'] leading-5 focus:outline-none cursor-pointer min-w-0 flex items-center justify-between w-full px-2 py-1"
        type="button"
        disabled={disabled}
      >
        <span className="truncate flex items-center gap-2">
          {icon && <span className="flex-shrink-0">{icon}</span>}
          <span>{selectedOption?.label || placeholder}</span>
        </span>
        <svg 
          className={`w-4 h-4 ml-2 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown options */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-72 overflow-hidden z-50 w-full">
          {/* Search input */}
          {showSearch && (
            <div className="p-2 border-b border-gray-200">
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search cities..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          )}
          
          {/* Options list */}
          <div className="max-h-48 overflow-auto scrollbar-hide">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No cities found
              </div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleOptionClick(option.value)}
                  className={`
                    px-4 py-2 cursor-pointer hover:bg-indigo-50 transition-colors
                    ${option.value === value ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700'}
                    ${option.disabled ? 'opacity-50 cursor-not-allowed' : ''}
                    flex items-center gap-2
                  `}
                >
                  {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
                  <span className="font-['Inter'] text-sm flex-1">{option.label}</span>
                  {option.badge && (
                    <span className="flex-shrink-0 text-xs bg-gray-200 px-2 py-0.5 rounded-full">
                      {option.badge}
                    </span>
                  )}
                  {option.value === value && (
                    <svg className="w-4 h-4 flex-shrink-0 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Citiescommondropdown;